/**
 * Basemap points of interest are the labels Mapbox renders from its own
 * vector tiles (restaurants, stations, parks, ...). They are not library POIs
 * and have no ID of ours; the Standard style exposes them through the "poi"
 * featureset, whose properties are documented at
 * https://docs.mapbox.com/map-styles/standard/guides/#featuresets.
 */
export interface BasemapPlace {
  /** Stable per-render feature identity, used to dedupe repeat clicks. */
  key: string;
  name: string;
  lat: number;
  lng: number;
  /** Broad category from the tiles, e.g. "restaurant" or "train_station". */
  class: string | null;
  /** Selector group: "poi", "transit", "airport", or "natural-point". */
  group: string | null;
  transitMode: string | null;
  transitNetwork: string | null;
  airportRef: string | null;
}

/**
 * Mapbox tile labels carry zero-width spaces as line-break hints, mostly in
 * CJK names (rendered as "イオン<ZWSP>フード<ZWSP>スタイル"). They are invisible
 * but survive into anything built from the name — a saved POI, a search URL —
 * so they are stripped here. Zero-width joiners and non-joiners are deliberately kept:
 * they are meaningful in Arabic, Persian, and Indic scripts.
 */
const INVISIBLE_NAME_CHARS = /[\u200b\u00ad\u200e\u200f\ufeff]/g;

function cleanName(value: string): string {
  return value.replace(INVISIBLE_NAME_CHARS, "").replace(/\s+/g, " ").trim();
}

function readString(
  properties: Record<string, unknown> | null | undefined,
  key: string,
): string | null {
  const value = properties?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

interface BasemapPoiFeature {
  id?: string | number;
  properties?: Record<string, unknown> | null;
  geometry?: { type: string; coordinates?: unknown } | null;
}

/**
 * Narrow a Mapbox featureset feature to the fields the popup needs. Returns
 * null for anything unusable — an unnamed label or a non-point geometry.
 */
export function basemapPlaceFromFeature(
  feature: BasemapPoiFeature | null | undefined,
): BasemapPlace | null {
  if (!feature) return null;

  const geometry = feature.geometry;
  if (!geometry || geometry.type !== "Point") return null;
  const coordinates = geometry.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;

  const [lng, lat] = coordinates;
  if (typeof lng !== "number" || typeof lat !== "number") return null;
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;

  const rawName = readString(feature.properties, "name");
  const name = rawName ? cleanName(rawName) : null;
  if (!name) return null;

  return {
    key: `${feature.id ?? name}:${lng.toFixed(6)},${lat.toFixed(6)}`,
    name,
    lat,
    lng,
    class: readString(feature.properties, "class"),
    group: readString(feature.properties, "group"),
    transitMode: readString(feature.properties, "transit_mode"),
    transitNetwork: readString(feature.properties, "transit_network"),
    airportRef: readString(feature.properties, "airport_ref"),
  };
}

function humanize(value: string): string {
  const words = value.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Short human-readable category line for the popup, or null when unknown. */
export function basemapPlaceCategory(place: BasemapPlace): string | null {
  if (place.group === "airport") {
    return place.airportRef ? `Airport · ${place.airportRef}` : "Airport";
  }

  if (place.group === "transit") {
    const mode = place.transitMode ? humanize(place.transitMode) : "Transit";
    return place.transitNetwork
      ? `${mode} · ${humanize(place.transitNetwork)}`
      : mode;
  }

  return place.class ? humanize(place.class) : null;
}
